import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import Navbar from '../components/Navbar';
import { getStoredUserId, getStoredTokens } from '../utils/auth';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import './CSS/Analytics.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtNum = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return iso; }
};

// ─── Sub-components ────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, color, subtitle, href }) => {
  const inner = (
    <div className="analytics-summary-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="analytics-summary-icon" style={{ color }}>{icon}</div>
      <div className="analytics-summary-value">{value}</div>
      <div className="analytics-summary-label">{label}</div>
      {subtitle && <div className="analytics-summary-subtitle">{subtitle}</div>}
    </div>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{inner}</a>
    : inner;
};

const ChartShell = ({ title, subtitle, loading, empty, emptyMsg, children }) => {
  let content;
  if (loading) {
    content = <div className="analytics-chart-loader"><span className="analytics-spinner" /></div>;
  } else if (empty) {
    content = <div className="analytics-chart-empty">{emptyMsg || 'No data available'}</div>;
  } else {
    content = children;
  }
  return (
    <div className="analytics-chart-card">
      <div className="analytics-chart-header">
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {content}
    </div>
  );
};

// OrgSelector — shows a dropdown when there are multiple orgs/pages.
// Locks items beyond maxAllowed with a 🔒 indicator.
// TODO: maxAllowed will come from GET /user/subscription once payment is integrated.
const OrgSelector = ({ items, selected, onSelect, maxAllowed, getLabel, getId }) => {
  if (!items || items.length <= 1) return null;
  return (
    <div className="analytics-org-selector-wrap">
      <select
        className="analytics-org-selector"
        value={getId(selected) || ''}
        onChange={(e) => {
          const found = items.find(item => getId(item) === e.target.value);
          if (found) onSelect(found);
        }}
      >
        {items.map((item, i) => {
          const locked = i >= maxAllowed;
          return (
            <option key={getId(item)} value={getId(item)} disabled={locked}>
              {locked ? `\uD83D\uDD12 ${getLabel(item)} \u2014 Upgrade to unlock` : getLabel(item)}
            </option>
          );
        })}
      </select>
    </div>
  );
};

const PlatformCard = ({ icon, color, name, stats, onDeepDive }) => (
  <div className="analytics-platform-card" style={{ borderTop: `3px solid ${color}` }}>
    <div className="analytics-platform-card-head">
      <span className="analytics-platform-card-icon" style={{ background: `${color}18`, color }}>{icon}</span>
      <span className="analytics-platform-card-name">{name}</span>
    </div>
    <div className="analytics-platform-card-stats">
      {stats.map(({ label, value }) => (
        <div key={label} className="analytics-platform-card-stat">
          <span className="analytics-platform-card-stat-value">{value}</span>
          <span className="analytics-platform-card-stat-label">{label}</span>
        </div>
      ))}
    </div>
    <button className="analytics-deep-dive-btn" onClick={onDeepDive} style={{ borderColor: color, color }}>
      View Full Analytics →
    </button>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const Analytics = () => {
  const navigate = useNavigate();

  // ── Summary bar state ───────────────────────────────────────────────────────
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summary, setSummary] = useState({
    linkedinFollowers: null,
    facebookFollowers: null,
    instagramFollowers: null,
    websiteScore: null,
  });

  // ── Platform card detail state ──────────────────────────────────────────────
  const [linkedinOrgName, setLinkedinOrgName] = useState('');
  const [instagramUsername, setInstagramUsername] = useState('');

  // ── Chart state ─────────────────────────────────────────────────────────────
  const [linkedinOrgId, setLinkedinOrgId] = useState(null);
  const [linkedinTimeline, setLinkedinTimeline] = useState([]);
  const [linkedinStats, setLinkedinStats] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(false);

  // ── AI insight state ────────────────────────────────────────────────────────
  const [aiInsight, setAiInsight] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(false);

  // ── Posts analytics state ──────────────────────────────────────────────────
  const [topPosts, setTopPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [contentTypeData, setContentTypeData] = useState([]);
  const [postsSummary, setPostsSummary] = useState(null);

  // ── LinkedIn connection state ─────────────────────────────────────────────────
  const [linkedinConnected, setLinkedinConnected] = useState(false);

  // ── Website scan state ────────────────────────────────────────────────────────
  const [latestScanUrl, setLatestScanUrl] = useState(null);

  // ── Org / page switcher state ───────────────────────────────────────────────
  const [linkedinOrgs, setLinkedinOrgs] = useState([]);
  const [selectedLinkedinOrg, setSelectedLinkedinOrg] = useState(null);
  const [facebookPages, setFacebookPages] = useState([]);
  const [selectedFacebookPage, setSelectedFacebookPage] = useState(null);

  // ── Package limits ──────────────────────────────────────────────────────────
  // TODO: Replace with value from GET /user/subscription once payment is integrated.
  // Example tiers: Starter = 1, Pro = 3, Enterprise = Infinity
  const MAX_LINKEDIN_ORGS = Infinity;
  const MAX_FACEBOOK_PAGES = Infinity;

  // ── Load summary + extract org/page IDs for charts ──────────────────────────
  const loadSummary = async () => {
    setSummaryLoading(true);
    const userid = getStoredUserId();
    if (!userid) { setSummaryLoading(false); return; }

    const tokens = getStoredTokens();
    const idToken = tokens?.idToken || '';
    const smHeaders = { userid };                               // social-media-mgmt service
    const bffHeaders = { Authorization: `Bearer ${idToken}` }; // BFF routes (authMiddleware)

    const [metaRes, linkedinRes, historyRes, linkedinStatusRes] = await Promise.allSettled([
      axios.get(`${API_BASE_URL}/meta/data`, { headers: smHeaders }),
      axios.get(`${API_BASE_URL}/linkedin/organizations`, { headers: smHeaders }),
      axios.get(`${API_BASE_URL}${API_ENDPOINTS.WEBPAGE_ANALYZER_HISTORY}`, { headers: bffHeaders }),
      axios.get(`${API_BASE_URL}/linkedin/auth/status`, { headers: smHeaders }),
    ]);

    let linkedinFollowers = null;
    let facebookFollowers = null;
    let instagramFollowers = null;
    let websiteScore = null;
    let primaryOrgId = null;

    // LinkedIn connection status — only load org data when truly connected
    const isLinkedInConnected = linkedinStatusRes.status === 'fulfilled' &&
      linkedinStatusRes.value.data?.connected === true;
    setLinkedinConnected(isLinkedInConnected);

    // LinkedIn — store all orgs, default to primary (only if connected)
    if (isLinkedInConnected && linkedinRes.status === 'fulfilled' && linkedinRes.value.data?.organizations) {
      const orgs = linkedinRes.value.data.organizations;
      setLinkedinOrgs(orgs);
      const primary = orgs.find(o => o.is_primary) || orgs[0];
      if (primary) {
        linkedinFollowers = primary.follower_count ?? null;
        primaryOrgId = primary.organization_id;
        setLinkedinOrgId(primary.organization_id);
        setLinkedinOrgName(primary.organization_name || '');
        setSelectedLinkedinOrg(primary);
      }
    }

    // Facebook + Instagram
    if (metaRes.status === 'fulfilled' && metaRes.value.data?.data) {
      const { facebook_pages = [], instagram_accounts = [] } = metaRes.value.data.data;
      setFacebookPages(facebook_pages);
      if (facebook_pages[0]) {
        setSelectedFacebookPage(facebook_pages[0]);
        facebookFollowers = facebook_pages[0].fan_count || facebook_pages[0].followers_count || 0;
      }
      instagramFollowers = instagram_accounts.reduce((sum, a) => sum + (a.followers_count || 0), 0);

      // Normalize Instagram accounts from pages if needed
      const igAccounts = instagram_accounts.length > 0
        ? instagram_accounts
        : facebook_pages.flatMap(p => {
            let ig = p.instagram_business_account;
            if (typeof ig === 'string') { try { ig = JSON.parse(ig); } catch { ig = null; } }
            return ig?.username ? [ig] : [];
          });
      if (igAccounts[0]) setInstagramUsername(igAccounts[0].username || '');
    }

    // Website score — latest scan
    // BFF response shape: { success, data: [...], count }
    if (historyRes.status === 'fulfilled') {
      const body = historyRes.value.data;
      const scans = body?.data || body?.history || body?.scans || (Array.isArray(body) ? body : []);
      const latest = scans[0] ?? null;
      if (latest) {
        // performanceScore is already 0-100 (added by web-scanner-service)
        const raw = latest.performanceScore ?? latest.scores?.performance ?? latest.performance_score ?? null;
        if (raw !== null) websiteScore = Math.round(raw <= 1 ? raw * 100 : raw);
        if (latest.url) setLatestScanUrl(latest.url);
      }
    }

    setSummary({ linkedinFollowers, facebookFollowers, instagramFollowers, websiteScore });
    setSummaryLoading(false);

    // Trigger LinkedIn engagement chart + posts after we have the org ID
    if (primaryOrgId) {
      loadLinkedinChart(primaryOrgId);
      loadLinkedinPosts(primaryOrgId);
    }
  };

  // ── LinkedIn engagement timeline ────────────────────────────────────────────
  const loadLinkedinChart = async (orgId) => {    if (!orgId) return;
    setChartsLoading(true);
    const userid = getStoredUserId();
    const headers = { userid };
    try {
      const res = await axios.get(`${API_BASE_URL}/linkedin/analytics/${orgId}?days=30`, { headers });
      if (res.data?.analytics) {
        const rows = [...res.data.analytics].reverse();
        setLinkedinTimeline(rows.map(r => ({
          date: fmtDate(r.date),
          Impressions: r.impressions || 0,
          Likes: r.likes || 0,
          Clicks: r.clicks || 0,
        })));
      }
      if (res.data?.summary) setLinkedinStats(res.data.summary);
    } catch { /* non-blocking */ }
    finally { setChartsLoading(false); }
  };

  // ── LinkedIn top posts + content type breakdown ─────────────────────────────
  const loadLinkedinPosts = async (orgId) => {
    if (!orgId) return;
    setPostsLoading(true);
    const userid = getStoredUserId();
    const headers = { userid };
    try {
      const res = await axios.get(
        `${API_BASE_URL}/linkedin/analytics/${orgId}/posts?limit=20`,
        { headers }
      );
      if (res.data?.top_posts) setTopPosts(res.data.top_posts.slice(0, 5));
      if (res.data?.summary) setPostsSummary(res.data.summary);
      const posts = res.data?.posts || res.data?.top_posts || [];
      if (posts.length > 0) {
        const typeMap = {};
        posts.forEach(p => {
          const t = (p.media_type || 'TEXT').replaceAll('_', ' ');
          if (!typeMap[t]) typeMap[t] = { count: 0, totalEng: 0 };
          typeMap[t].count++;
          typeMap[t].totalEng += (p.engagement_rate || 0);
        });
        setContentTypeData(
          Object.entries(typeMap)
            .map(([type, d]) => ({
              type,
              avgEngagement: +(d.totalEng / d.count * 100).toFixed(2),
              count: d.count,
            }))
            .sort((a, b) => b.avgEngagement - a.avgEngagement)
        );
      }
    } catch { /* non-blocking */ }
    finally { setPostsLoading(false); }
  };

  // ── Org / page switch handlers ─────────────────────────────────────────────
  const handleLinkedinOrgChange = (org) => {
    setSelectedLinkedinOrg(org);
    setLinkedinOrgId(org.organization_id);
    setLinkedinOrgName(org.organization_name || '');
    setLinkedinStats(null);
    setLinkedinTimeline([]);
    setTopPosts([]);
    setContentTypeData([]);
    setPostsSummary(null);
    setSummary(prev => ({ ...prev, linkedinFollowers: org.follower_count ?? null }));
    loadLinkedinChart(org.organization_id);
    loadLinkedinPosts(org.organization_id);
  };

  const handleFacebookPageChange = (page) => {
    setSelectedFacebookPage(page);
    setSummary(prev => ({ ...prev, facebookFollowers: page.fan_count || page.followers_count || 0 }));
  };

  useEffect(() => { loadSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── AI insights ─────────────────────────────────────────────────────────────
  const fetchAiInsight = async () => {
    setAiLoading(true);
    setAiError(false);
    setAiInsight('');
    const userid = getStoredUserId();
    if (!userid) { setAiLoading(false); return; }
    const tokens = getStoredTokens();
    const idToken = tokens?.idToken || '';
    try {
      const res = await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.CHATBOT}`,
        {
          message: [
            'Here is my current social media analytics data:',
            `- LinkedIn followers: ${summary.linkedinFollowers ?? 'N/A'}`,
            `- Facebook followers: ${summary.facebookFollowers ?? 'N/A'}`,
            `- Instagram followers: ${summary.instagramFollowers ?? 'N/A'}`,
            `- Website SEO score: ${summary.websiteScore != null ? summary.websiteScore + '/100' : 'N/A'}`,
            postsSummary ? `- LinkedIn 30-day: ${fmtNum(postsSummary.total_impressions)} impressions, ${fmtNum(postsSummary.total_likes)} likes, ${fmtNum(postsSummary.total_comments)} comments` : '',
            contentTypeData[0] ? `- Best content type: ${contentTypeData[0].type} (${contentTypeData[0].avgEngagement}% avg engagement)` : '',
            topPosts[0] ? `- Top post: "${(topPosts[0].post_text || '').slice(0, 80)}..." — ${fmtNum(topPosts[0].impressions)} impressions` : '',
            '',
            'Briefly summarize this in 3 bullet points. Focus on what is working and one area to improve. Keep it under 100 words.',
          ].filter(Boolean).join('\n'),
          userId: userid,
        },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      // BFF returns: { success, reply, mode, ... }
      setAiInsight(res.data?.reply || res.data?.response || res.data?.message || '');
    } catch {
      setAiError(true);
    } finally {
      setAiLoading(false);
    }
  };

  // ── Audience bar chart data ──────────────────────────────────────────────────
  const audienceData = [
    { platform: 'LinkedIn', followers: summary.linkedinFollowers || 0, fill: '#0a66c2' },
    { platform: 'Facebook', followers: summary.facebookFollowers || 0, fill: '#1877f2' },
    { platform: 'Instagram', followers: summary.instagramFollowers || 0, fill: '#e1306c' },
  ];
  const audienceEmpty = audienceData.every(d => d.followers === 0);

  // ── Derived values ────────────────────────────────────────────────────────
  const websiteScoreLabel = summaryLoading ? '...' : (summary.websiteScore !== null ? `${summary.websiteScore}/100` : '—');
  const aiButtonLabel = (() => { if (aiLoading) return 'Thinking...'; if (aiInsight) return 'Refresh'; return 'Get Insights'; })();

  // ── Content type chart config (computed before render to avoid IIFE in JSX)
  const ctAllEngZero = contentTypeData.every(d => d.avgEngagement === 0);
  const ctBarKey = ctAllEngZero ? 'count' : 'avgEngagement';
  const ctTickFmt = ctAllEngZero ? v => String(v) : v => `${v}%`;
  const ctTtFmt = ctAllEngZero ? (v) => [v, 'Posts'] : (v) => [`${v}%`, 'Avg Engagement'];
  const ctSubtitle = ctAllEngZero
    ? 'Number of posts per content type (LinkedIn)'
    : 'Average engagement rate by content type (LinkedIn)';

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="analytics-page">
      <Navbar />
      <main className="analytics-shell">

        {/* ── Page header ── */}
        <div className="analytics-header">
          <p className="analytics-eyebrow">OVERVIEW</p>
          <h1 className="analytics-title">Analytics Dashboard</h1>
          <p className="analytics-subtitle">Cross-platform performance at a glance</p>
        </div>

        {/* ── AI Insights card ── */}
        <div className="analytics-ai-card">
          <div className="analytics-ai-header">
            <div>
              <h3>✨ AI Insights</h3>
              <p>Get an AI-generated summary of your social media performance</p>
            </div>
            <button className="analytics-ai-btn" onClick={fetchAiInsight} disabled={aiLoading}>
              {aiButtonLabel}
            </button>
          </div>
          {aiInsight && <div className="analytics-ai-response">{aiInsight}</div>}
          {aiError && <p className="analytics-ai-error">Could not load insights. Please try again.</p>}
        </div>

        {/* ── Summary bar ── */}
        <div className="analytics-summary-bar">
          <SummaryCard icon="💼" label="LinkedIn Followers"
            value={summaryLoading ? '...' : linkedinConnected ? fmtNum(summary.linkedinFollowers) : '—'} color="#0a66c2" />
          <SummaryCard icon="📘" label="Facebook Followers"
            value={summaryLoading ? '...' : fmtNum(summary.facebookFollowers)} color="#1877f2" />
          <SummaryCard icon="📸" label="Instagram Followers"
            value={summaryLoading ? '...' : fmtNum(summary.instagramFollowers)} color="#e1306c" />
          <SummaryCard icon="🌐" label="SEO Score"
            value={websiteScoreLabel} color="#10b981" />
          <SummaryCard icon="🔍" label="Analysed Website"
            value={summaryLoading ? '...' : latestScanUrl ? latestScanUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '').split('/')[0] : '—'}
            color="#6366f1"
            href={latestScanUrl || undefined} />
        </div>

        {/* ── Comparison charts row ── */}
        <div className="analytics-charts-row">

          {/* Audience comparison */}
          <ChartShell
            title="Audience Comparison"
            subtitle="Total followers per platform"
            loading={summaryLoading}
            empty={audienceEmpty}
            emptyMsg="Connect social accounts to see audience data"
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={audienceData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                barSize={48}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="platform" axisLine={false} tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 13 }} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={fmtNum}
                  tick={{ fill: '#94a3b8', fontSize: 12 }} width={48} />
                <Tooltip formatter={(v) => [fmtNum(v), 'Followers']}
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                <Bar dataKey="followers" radius={[6, 6, 0, 0]}>
                  {audienceData.map((entry) => <Cell key={entry.platform} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartShell>

          {/* LinkedIn engagement timeline */}
          <ChartShell
            title="LinkedIn Engagement"
            subtitle={linkedinOrgName ? `${linkedinOrgName} — last 30 days` : 'Last 30 days'}
            loading={chartsLoading}
            empty={linkedinTimeline.length === 0}
            emptyMsg={linkedinOrgId ? 'No engagement data yet for this period' : 'Connect LinkedIn to see engagement trends'}
          >
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={linkedinTimeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradImpressions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0a66c2" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#0a66c2" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradLikes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" axisLine={false} tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  interval={Math.floor(linkedinTimeline.length / 5)} />
                <YAxis axisLine={false} tickLine={false} tickFormatter={fmtNum}
                  tick={{ fill: '#94a3b8', fontSize: 12 }} width={44} />
                <Tooltip formatter={(v) => fmtNum(v)}
                  contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Area type="monotone" dataKey="Impressions" stroke="#0a66c2" strokeWidth={2}
                  fill="url(#gradImpressions)" dot={false} />
                <Area type="monotone" dataKey="Likes" stroke="#10b981" strokeWidth={2}
                  fill="url(#gradLikes)" dot={false} />
                <Area type="monotone" dataKey="Clicks" stroke="#f59e0b" strokeWidth={2}
                  fill="none" dot={false} strokeDasharray="4 2" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartShell>

        </div>

        {/* ── Platform deep-dive cards ── */}
        <div className="analytics-platform-cards">
          {/* LinkedIn card — with org switcher */}
          {!linkedinConnected ? (
            <div className="analytics-platform-card" style={{ borderTop: '3px solid #0a66c2', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div className="analytics-platform-card-head">
                <span className="analytics-platform-card-icon" style={{ background: '#0a66c218', color: '#0a66c2' }}>💼</span>
                <span className="analytics-platform-card-name">LinkedIn</span>
              </div>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                Connect your LinkedIn account to see follower counts, post impressions, and engagement data.
              </p>
              <button className="analytics-deep-dive-btn"
                onClick={() => navigate('/social-media-automation')}
                style={{ borderColor: '#0a66c2', color: '#0a66c2' }}>
                Connect LinkedIn →
              </button>
            </div>
          ) : (
          <div className="analytics-platform-card" style={{ borderTop: '3px solid #0a66c2' }}>
            <div className="analytics-platform-card-head">
              <span className="analytics-platform-card-icon" style={{ background: '#0a66c218', color: '#0a66c2' }}>💼</span>
              <span className="analytics-platform-card-name">LinkedIn</span>
            </div>
            <OrgSelector
              items={linkedinOrgs}
              selected={selectedLinkedinOrg}
              onSelect={handleLinkedinOrgChange}
              maxAllowed={MAX_LINKEDIN_ORGS}
              getLabel={(o) => o.organization_name || o.organization_id}
              getId={(o) => o?.organization_id || ''}
            />
            <div className="analytics-platform-card-stats">
              {[
                { label: 'Followers', value: fmtNum(summary.linkedinFollowers) },
                { label: '30d Impressions', value: fmtNum(linkedinStats?.totalImpressions ?? linkedinStats?.impressions) },
                { label: 'Total Likes', value: fmtNum(linkedinStats?.totalLikes ?? linkedinStats?.likes) },
              ].map(({ label, value }) => (
                <div key={label} className="analytics-platform-card-stat">
                  <span className="analytics-platform-card-stat-value">{value}</span>
                  <span className="analytics-platform-card-stat-label">{label}</span>
                </div>
              ))}
            </div>
            <button className="analytics-deep-dive-btn"
              onClick={() => navigate('/social-media-automation')}
              style={{ borderColor: '#0a66c2', color: '#0a66c2' }}>
              View Full Analytics →
            </button>
          </div>
          )}

          {/* Facebook card — with page switcher */}
          <div className="analytics-platform-card" style={{ borderTop: '3px solid #1877f2' }}>
            <div className="analytics-platform-card-head">
              <span className="analytics-platform-card-icon" style={{ background: '#1877f218', color: '#1877f2' }}>📘</span>
              <span className="analytics-platform-card-name">Facebook</span>
            </div>
            <OrgSelector
              items={facebookPages}
              selected={selectedFacebookPage}
              onSelect={handleFacebookPageChange}
              maxAllowed={MAX_FACEBOOK_PAGES}
              getLabel={(p) => p.page_name || p.page_id}
              getId={(p) => p?.page_id || ''}
            />
            <div className="analytics-platform-card-stats">
              {[
                { label: 'Page Fans', value: fmtNum(summary.facebookFollowers) },
                { label: 'Page', value: selectedFacebookPage?.page_name || '—' },
                { label: 'Status', value: summary.facebookFollowers ? 'Connected' : 'Not connected' },
              ].map(({ label, value }) => (
                <div key={label} className="analytics-platform-card-stat">
                  <span className="analytics-platform-card-stat-value">{value}</span>
                  <span className="analytics-platform-card-stat-label">{label}</span>
                </div>
              ))}
            </div>
            <button className="analytics-deep-dive-btn"
              onClick={() => navigate('/social-media-automation')}
              style={{ borderColor: '#1877f2', color: '#1877f2' }}>
              View Full Analytics →
            </button>
          </div>
          <PlatformCard
            icon="📸" color="#e1306c" name={instagramUsername ? `@${instagramUsername}` : 'Instagram'}
            stats={[
              { label: 'Followers', value: fmtNum(summary.instagramFollowers) },
              { label: 'Platform', value: 'Meta / Instagram' },
              { label: 'Status', value: summary.instagramFollowers ? 'Connected' : 'Not connected' },
            ]}
            onDeepDive={() => navigate('/social-media-automation')}
          />
        </div>

        {/* ── Content Type Breakdown ── */}
        {(postsLoading || contentTypeData.length > 0) && (
          <div className="analytics-charts-row">
            <ChartShell
              title="Content Type Distribution"
              subtitle={ctSubtitle}
              loading={postsLoading}
              empty={contentTypeData.length === 0}
              emptyMsg="No post data yet"
            >
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={contentTypeData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barSize={44}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="type" axisLine={false} tickLine={false}
                    tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false}
                    tickFormatter={ctTickFmt}
                    tick={{ fill: '#94a3b8', fontSize: 12 }} width={48} />
                  <Tooltip formatter={ctTtFmt}
                    contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }} />
                  <Bar dataKey={ctBarKey} radius={[6, 6, 0, 0]}>
                    {contentTypeData.map((entry, i) => (
                      <Cell key={entry.type} fill={['#0a66c2', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'][i % 5]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartShell>
            </div>
          )}

        {/* ── Best Performing Posts ── */}
        <div className="analytics-chart-card">
          <div className="analytics-chart-header">
            <h3>🔥 Best Performing Posts</h3>
            <p>Top LinkedIn posts by engagement rate</p>
          </div>
          {postsLoading ? (
            <div className="analytics-chart-loader"><span className="analytics-spinner" /></div>
          ) : topPosts.length === 0 ? (
            <div className="analytics-chart-empty">
              {linkedinOrgId ? 'No posts found — try syncing your LinkedIn data' : 'Connect LinkedIn to see top posts'}
            </div>
          ) : (
            <div className="analytics-top-posts">
              {topPosts.map((post) => (
                <div className="analytics-post-row" key={post.post_id}>
                  <div className="analytics-post-header">
                    <p className="analytics-post-text">
                      {post.post_text
                        ? post.post_text.slice(0, 120) + (post.post_text.length > 120 ? '…' : '')
                        : '(No text)'}
                    </p>
                    {post.media_type && (
                      <span className="analytics-post-badge">{post.media_type.replaceAll('_', ' ')}</span>
                    )}
                  </div>
                  <div className="analytics-post-stats">
                    <span className="analytics-post-stat">👁 {fmtNum(post.impressions)}</span>
                    <span className="analytics-post-stat">❤️ {fmtNum(post.likes)}</span>
                    <span className="analytics-post-stat">💬 {fmtNum(post.comments)}</span>
                    <span className="analytics-post-stat">🔁 {fmtNum(post.shares)}</span>
                    {post.engagement_rate > 0 && (
                      <span className="analytics-post-eng-pill">
                        {(post.engagement_rate * 100).toFixed(2)}% eng
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>


      </main>
    </div>
  );
};

export default Analytics;
